import axios from "axios"
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from "./auth"
import { getStoredLanguage } from "./i18n"
import { env } from "@/config/env"

const api = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeout,
  headers: { "Content-Type": "application/json" },
})

// Attach access token and language to every request
api.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  config.headers["Accept-Language"] = getStoredLanguage()
  // İstek giderken elde olan yenileme anahtarı: 401 dönünce, arada başka bir
  // sekmenin oturumu zaten yenileyip yenilemediğini bununla anlıyoruz.
  ;(config as { _gonderilenRefresh?: string | null })._gonderilenRefresh = getRefreshToken()
  return config
})

function girisSayfasinaDon() {
  clearTokens()
  if (window.location.pathname !== "/giris") window.location.href = "/giris"
}

/**
 * Oturumu yeniler ve geçerli erişim anahtarını döndürür.
 *
 * Yenileme anahtarı her kullanımda DEĞİŞİYOR (sunucu eskisini siliyor). Aynı
 * tarayıcıda iki sekme açıkken boşta beklerken ikisi de aynı anda 401 alıp
 * aynı eski anahtarla yenilemeye gidiyordu: biri başarıyor, öteki "geçersiz
 * anahtar" alıp anahtarları siliyor — ortak `localStorage` yüzünden iki sekme
 * birden çıkış yapıyordu.
 *
 * Çözüm: yenileme sekmeler arası KİLİTLE (Web Locks) tek tek yapılıyor ve
 * kilidi alan önce bakıyor: anahtar, isteğin gönderildiği andakinden farklıysa
 * başka biri zaten yenilemiş demektir — yeni anahtar kullanılır, sunucuya
 * gidilmez. Aynı sekmedeki eşzamanlı istekler de böylece tek yenilemede
 * birleşiyor.
 */
async function oturumuYenile(gonderilenRefresh: string | null | undefined): Promise<string> {
  const yenile = async () => {
    const guncel = getRefreshToken()
    if (!guncel) throw new OturumBitti()
    const access = getAccessToken()
    if (gonderilenRefresh && guncel !== gonderilenRefresh && access) return access
    const { data } = await axios.post(`${env.apiBaseUrl}/auth/refresh`, { refreshToken: guncel })
    setTokens(data)
    return data.accessToken as string
  }
  // Bu sekmede süren yenileme varsa ona katılınır (kilit desteklenmese de tek istek).
  if (!suren) {
    const kilit = typeof navigator !== "undefined" ? navigator.locks : undefined
    const is: Promise<string> = kilit ? kilit.request("audex-oturum-yenileme", yenile).then((t) => t) : yenile()
    suren = is.finally(() => {
      suren = null
    })
  }
  return suren as Promise<string>
}

let suren: Promise<string> | null = null

class OturumBitti extends Error {}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config

    /*
     * Ağ hatası / zaman aşımı (yanıt YOK) oturumun bittiği anlamına gelmiyor:
     * bilgisayar uykudan dönerken, Wi-Fi bir an koptuğunda ya da sunucu
     * yeniden başlarken arka plandaki yoklamalar (bildirim sayacı, sohbet)
     * böyle düşüyor. Eskiden burada anahtarlar silinip girişe atılıyordu —
     * boşta bekleyen kullanıcı "kendi kendine çıkış yaptı" görüyordu.
     */
    if (!error.response) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Don't intercept auth endpoints - let the caller handle the error
      const isAuthRequest = originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/register')
      if (isAuthRequest) {
        return Promise.reject(error)
      }

      originalRequest._retry = true
      try {
        const token = await oturumuYenile(originalRequest._gonderilenRefresh)
        originalRequest.headers.Authorization = `Bearer ${token}`
        return api(originalRequest)
      } catch (refreshError) {
        // Yenileme isteği ağ yüzünden düştüyse oturum hâlâ geçerli olabilir.
        const agHatasi = axios.isAxiosError(refreshError) && !refreshError.response
        if (!agHatasi) girisSayfasinaDon()
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  },
)

export default api
